import React, { useCallback } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  Space,
  App,
  Tooltip
} from 'antd'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, HelpCircle } from 'lucide-react'
import { useCompanyStore } from '../../../store/company/companyStore'
import { useApi } from '../../../services/ApiContext'
import { TeamMember } from '../../../types/ffe'
import { ApiError } from '../../../services/types'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'

const { Title, Text } = Typography

const roleDescriptions = {
  administrator: {
    title: 'Administrator',
    description: 'Full system access including:',
    permissions: [
      'Manage all documents and projects',
      'Manage team members and roles',
      'Configure system settings',
      'Manage integrations',
      'Access billing and subscription settings',
      'View audit logs and activity reports'
    ]
  },
  designer: {
    title: 'Designer',
    description: 'Standard user access including:',
    permissions: [
      'Manage all documents and projects',
      'Export documents',
      'Access shared team resources'
    ]
  }
}

export const NewUserPage: React.FC = () => {
  const [form] = Form.useForm()
  const [captcha, setCaptcha] = React.useState<string | null>(null)
  const [refreshReCaptcha, setRefreshReCaptcha] = React.useState(0)
  const navigate = useNavigate()
  const { message } = App.useApp()
  const user = {
    email: '',
    firstName: '',
    lastName: '',
    roles: ['basic']
  }
  const addTeamMember = useCompanyStore((state) => state.addTeamMember)
  const api = useApi()

  const onVerify = useCallback((token: string | null) => {
    setCaptcha(token)
  }, [])

  React.useEffect(() => {
    if (user) {
      form.setFieldsValue(user)
    }
  }, [user, form])

  const handleSubmit = async (values: TeamMember) => {
    if (!captcha) return
    try {
      const newTeamMember = await api.inviteTeamMember(
        {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          roles: values.roles
        },
        captcha
      )
      addTeamMember(newTeamMember)
      message.success(`Invitation sent to ${values.email}`)
    } catch (error) {
      if (error instanceof ApiError) {
        message.error(error.message)
      } else {
        message.error('An error occurred')
      }
    }
    setRefreshReCaptcha((r) => r + 1)
  }

  const renderRoleTooltip = (role: keyof typeof roleDescriptions) => (
    <div className="max-w-xs">
      <Text strong className="block mb-2 text-white">
        {roleDescriptions[role].description}
      </Text>
      <ul className="list-disc pl-4 space-y-1">
        {roleDescriptions[role].permissions.map((permission, index) => (
          <li key={index} className="text-sm">
            {permission}
          </li>
        ))}
      </ul>
    </div>
  )

  if (!user) {
    navigate('/settings/users')
    return null
  }

  return (
    <div className="space-y-6">
      <GoogleReCaptcha
        refreshReCaptcha={refreshReCaptcha}
        onVerify={onVerify}
      />
      <div className="flex flex-col items-start gap-2">
        <Button
          type="text"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/app/settings/users')}
        >
          Back to Team Members
        </Button>
        <div>
          <Title level={4} className="!mb-0">
            Invite Team Member
          </Title>
          <Text type="secondary">
            Invite a new team member to your organization, they will receive an
            email to activate their account.
          </Text>
        </div>
      </div>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="flex gap-4 w-full">
            <Form.Item
              className="flex-1"
              name="firstName"
              label="First Name"
              rules={[
                {
                  required: true,
                  message: "Please enter this team member's first name"
                }
              ]}
            >
              <Input placeholder="Enter team member's first name" />
            </Form.Item>

            <Form.Item
              className="flex-1"
              name="lastName"
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: "Please enter this team member's last name"
                }
              ]}
            >
              <Input placeholder="Enter team member's last name" />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter team member's email address" />
          </Form.Item>

          <Form.Item
            className="max-w-[400px]"
            name="roles"
            label={
              <Space>
                Role
                <Tooltip
                  title="Click on a role to see detailed permissions"
                  placement="right"
                >
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              value={user.roles[0]}
              onChange={(value) => form.setFieldsValue({ roles: [value] })}
              options={[
                {
                  label: (
                    <Tooltip
                      title={renderRoleTooltip('administrator')}
                      placement="right"
                      overlayClassName="w-80"
                    >
                      <div>Administrator</div>
                    </Tooltip>
                  ),
                  value: 'orgManager'
                },
                {
                  label: (
                    <Tooltip
                      title={renderRoleTooltip('designer')}
                      placement="right"
                      overlayClassName="w-80"
                    >
                      <div>Designer</div>
                    </Tooltip>
                  ),
                  value: 'basic'
                }
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Send Invite
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
